import { create } from 'zustand';
import {
  generateImage,
  getImageUrl,
  ImageGenerationParams,
  DEFAULT_PARAMS,
} from '../services/imageService';

export interface GeneratedImage {
  id: string;
  prompt: string;
  imageUrl: string;
  params: ImageGenerationParams;
  createdAt: number;
}

interface ImageStore {
  params: ImageGenerationParams;
  isGenerating: boolean;
  generatedImages: GeneratedImage[];
  error: string | null;

  setParams: (partial: Partial<ImageGenerationParams>) => void;
  resetParams: () => void;
  generate: () => Promise<void>;
  removeImage: (id: string) => void;
  clearImages: () => void;
  clearError: () => void;
}

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

export const useImageStore = create<ImageStore>((set, get) => ({
  params: { ...DEFAULT_PARAMS },
  isGenerating: false,
  generatedImages: [],
  error: null,

  setParams: (partial) => {
    set((state) => ({ params: { ...state.params, ...partial } }));
  },

  resetParams: () => {
    set({ params: { ...DEFAULT_PARAMS } });
  },

  clearError: () => {
    if (get().error !== null) set({ error: null });
  },

  generate: async () => {
    const { params } = get();
    if (!params.prompt.trim()) {
      set({ error: 'Please enter a prompt.' });
      return;
    }

    set({ isGenerating: true, error: null });

    try {
      const response = await generateImage(params);
      const imageUrl = getImageUrl(response.image_url);

      const newImage: GeneratedImage = {
        id: generateId(),
        prompt: params.prompt,
        imageUrl,
        params: { ...params },
        createdAt: Date.now(),
      };

      set((state) => ({
        generatedImages: [newImage, ...state.generatedImages],
        isGenerating: false,
      }));
    } catch (error: any) {
      set({
        error: error?.message || 'Failed to generate image. Please try again.',
        isGenerating: false,
      });
    }
  },

  removeImage: (id) => {
    set((state) => ({
      generatedImages: state.generatedImages.filter((img) => img.id !== id),
    }));
  },

  clearImages: () => {
    set({ generatedImages: [] });
  },
}));